/**
 * The docket, the meeting, and the record — phases 2, 3 and 4.
 *
 * ONE RULE SHAPES ALL OF IT: voting is reachable on the item a commissioner has opened, in the
 * meeting, and nowhere else. `domain/meeting.ts` decides that; this file asks and obeys. A GM who
 * cannot vote is never shown three dead buttons — he is told which of the four reasons applies,
 * because "nothing happens when I press it" is the failure a gate is supposed to prevent.
 *
 * Before the meeting the docket is readable and unvotable, and a GM may record a PRIVATE
 * PROVISIONAL vote with a note. That is not a vote: it lives on its own field, it never reaches a
 * tally, and it exists so somebody who cannot attend has said what he thinks before the argument
 * starts rather than after.
 *
 * The prototype has a phase switcher. It is a prototype affordance and is deliberately not built —
 * the phase is derived from the docket, and each transition is an act with its own meaning:
 * publishing, opening the first item, committing.
 */

import { useEffect, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import {
  LEAGUE_SIZE, OUTCOME_LABEL, RELAXED_MAJORITY, SUPER_MAJORITY, tallyFrom, tallyOutcome,
  votesStillNeeded, type Vote,
} from '../domain/proposals.js';
import {
  type Phase, canVote, itemState, openItem, provenance, recordProvisional,
  recordVote, recordedProposal, reopenItem, tableItem,
} from '../domain/meeting.js';
import { GmBadge } from './GmBadge.js';
import { Citations } from './RuleText.js';
import { GMS } from '../store/session.js';
import type { Docket, GmId, LeagueStore, Role, RuleProposal } from '../store/types.js';
import { atLeast } from '../store/types.js';
import { RichView } from './Rich.js';
import {
  type ProxyBallot, canRecordAmended, instructionFor, isLodged, representedSplit,
} from '../domain/proxy.js';
import { decidedRow, roomStatus, thresholdTrack, yesSplit } from '../domain/room.js';

const VOTES: Vote[] = ['yes', 'no', 'abstain'];

export interface MeetingProps {
  phase: Phase;
  agenda: RuleProposal[];
  docket: Docket;
  /** Every LODGED proxy — `firestore.rules` will not return an unlodged one to anybody but its author. */
  proxies?: readonly ProxyBallot[];
  gm: string;
  actor: string;
  role: Role;
  store: LeagueStore;
}

export function Meeting({
  phase, agenda, docket, proxies = [], gm, actor, role, store,
}: MeetingProps) {
  const commish = atLeast(role, 'commissioner');
  const now = () => new Date().toISOString();

  const saveDocket = (d: Docket) => void store.saveDocket(d);
  const saveProposal = (p: RuleProposal) => void store.saveProposal(p);

  /*
   * THE ROOM IS A STATE, NOT A DESTINATION (handoff §3). Same component, same items, same gate —
   * what changes when the first item opens is the frame around them: near-black, a status bar
   * counting the quorum out loud, and the agenda beside the item instead of above it. Nothing
   * about voting moves, which is the point of doing it this way rather than building a second
   * screen that has to be kept in step with this one.
   */
  const inRoom = phase === 'meeting';
  const status = roomStatus({
    gms: GMS,
    absent: docket.absent ?? [],
    proxies,
    order: agenda.map((p) => p.id),
    openItemId: docket.openItemId,
    closedIds: docket.closedIds,
  });

  /*
   * THE STAGE STAYS IN DOCKET ORDER, and the live item is scrolled to instead.
   *
   * It used to sort the live item to the top, which fixed a real problem — on a phone the vote was
   * a screen and a half below the fold — and caused a worse one. Every time a commissioner opened
   * an item the list re-ordered under him, so the row he was about to press moved. Tim, after the
   * 2026 committee: "I couldn't get the 3rd item to open up until I switched to impersonate a GM
   * and then switched back." A list that rearranges itself between the eye and the finger is
   * exactly that experience, and re-rendering settles it.
   *
   * Bringing the open item into view gets the same thing the sort was for without moving anything
   * anybody is aiming at.
   */
  const staged = agenda.map((p, i) => ({ p, n: i + 1 }));

  /*
   * Scrolled, not re-ordered. Only when the OPEN ITEM changes — not on every render — so a
   * commissioner reading down the board is not yanked back to the live row every time somebody
   * votes.
   */
  useEffect(() => {
    if (!inRoom || !docket.openItemId || typeof document === 'undefined') return;
    const el = document.getElementById(`item-${docket.openItemId}`);
    el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [inRoom, docket.openItemId]);

  const items = (
    <ol className="mtitems">
      {staged.map(({ p, n: i }) => (
        <Item
          key={p.id} n={i} p={p} phase={phase} docket={docket}
          gm={gm} actor={actor} commish={commish} proxies={proxies}
          onOpen={() => saveDocket(openItem(docket, p.id, now()))}
          onReopen={() => saveDocket(reopenItem(docket, p.id, now()))}
          onTable={() => saveDocket(tableItem(docket, now(), agenda))}
          onVote={(voter, v) => saveProposal(recordVote(p, voter, v, actor.toUpperCase(), now()))}
          onProvisional={(entry) =>
            saveProposal(recordProvisional(p, gm.toUpperCase(), entry, now()))}
          /* One act, two doors — see `recordedProposal`. The console's primary ends here too. */
          onRecord={() => saveProposal(
            recordedProposal(p, docket.represented ?? GMS.length, now()))}
        />
      ))}
    </ol>
  );

  return (
    <div className={`meeting ${inRoom ? 'room' : ''}`}>
      {inRoom ? (
        <>
          <div className="mtroomtop">
            <span className="mtlivetag"><i aria-hidden="true" />In session</span>
            <span className="mtquorum">
              {status.line}
              {status.where && <> · <span className="mtwhere">{status.where}</span></>}
            </span>
            <span className="mtshape">{status.shape}</span>
          </div>
          <div className="mtroombody">
            <Rail agenda={agenda} docket={docket} proxies={proxies} />
            <div className="mtstage">{items}</div>
          </div>
        </>
      ) : (
        <>
          {/*
            THE THRESHOLDS ARE ON EVERY VOTE, not in a help panel. Seven and six are the numbers
            the room is counting to, and abstaining counting as a no is the part people get wrong
            out loud every year.

            In the room it is drawn instead — see `ThresholdTrack`. The handoff's reasoning: "The
            header no longer repeats the thresholds in prose."
          */}
          <p className="mtthresh">
            <b>7 yes</b> makes a rule permanent · <b>6</b> carries it one season only ·
            {' '}abstaining counts as a no
            <Citations ids={['2015-I-1', '2019-I-2', '2020-I-1']} />
          </p>
          {items}
        </>
      )}

      {/*
        COMMITTING MOVED TO THE COMMISSIONER CONSOLE (handoff §2). It was a card at the foot of
        this list with its own arming step — correct in itself, and in the wrong place: it is one
        of the three acts that move the committee year, and all three now stand in the same bar,
        separated from anything reversible. What it used to enumerate, the console's arming state
        says.
      */}
      {phase === 'committed'
        && <p className="note good">Committed to the record. This docket is history now.</p>}
    </div>
  );
}

/**
 * The agenda rail — what has been decided, what is happening, what is left.
 *
 * A DECISION HAS TO READ FROM ACROSS A TABLE, so a closed item is not small grey text. It carries
 * the outcome as a WORD, the margin, and where it applies the consequence a bare "Failed" hides:
 * a re-vote that falls short takes a rule that is in force today out of the book tonight.
 *
 * Word plus rail, never colour alone. Red on near-black is the weakest contrast pair available
 * and a colour-blind reader gets nothing from it. And GREEN IS THE LIVE ITEM'S ALONE — a decided
 * item that glows competes with the one that is actually happening.
 */
function Rail(
  { agenda, docket, proxies }:
  { agenda: RuleProposal[]; docket: Docket; proxies: readonly ProxyBallot[] },
) {
  const absent = docket.absent ?? [];
  return (
    <nav className="mtrail" aria-label="Agenda">
      <p className="mtrailhd">Agenda</p>
      <ol>
        {agenda.map((p, i) => {
          const state = itemState(p.id, docket);
          const votes = (p.votes ?? {}) as Record<string, Vote>;
          const represented = representedSplit(GMS, absent, proxies).total;
          const tally = tallyFrom(votes, represented);
          const result = tallyOutcome(tally, {
            isRevote: p.isRevote, proposerObjects: p.proposerObjects,
          });
          const done = state === 'closed' || Boolean(p.recordedAt);
          const row = done ? decidedRow(p, tally, result.outcome) : null;
          return (
            <li key={p.id} className={`mtrailrow ${state} ${row ? row.tone : ''}`}>
              <a href={`#item-${p.id}`}>
                <span className="mtrailno">{i + 1}</span>
                <span className="mtrailtitle">{p.title}</span>
                {row ? (
                  <span className="mtrailout">
                    <b className={row.tone}>{row.word}</b>
                    <span className="mtrailmargin">{row.margin}</span>
                    {row.flag && <span className="mtrailflag">{row.flag}</span>}
                  </span>
                ) : (
                  <span className="mtrailout">
                    <b className="pending">{state === 'live' ? 'voting' : 'to come'}</b>
                  </span>
                )}
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/**
 * The threshold track — the thing that decides pass or fail, drawn rather than left as arithmetic.
 *
 * Ten seats. A yes cast in the room fills white; one cast on a lodged proxy fills green, because
 * "we have six" and "we have six, two of whom are not here" are different sentences and the second
 * one is the one somebody will query. The marks sit at the two numbers that matter, and the line
 * underneath says what the count MEANS — never a bare figure, which is the same rule
 * `thresholdNote` follows everywhere else.
 */
function ThresholdTrack(
  { p, votes, represented, absent, proxies }: {
    p: RuleProposal;
    votes: Record<string, Vote>;
    represented: number;
    absent: readonly string[];
    proxies: readonly ProxyBallot[];
  },
) {
  const split = yesSplit(votes, absent, proxies);
  const track = thresholdTrack({
    yesInRoom: split.room,
    yesByProxy: split.proxy,
    represented,
    isRevote: p.isRevote,
    proposerObjects: p.proposerObjects,
  });
  const seats = track.cells.length;

  return (
    <div className={`mttrack ${track.standing}`}>
      <ol className="mtcells" aria-hidden="true">
        {track.cells.map((c, i) => <li key={i} className={c} />)}
      </ol>
      <div className="mtticks" aria-hidden="true">
        {track.ticks.map((t) => (
          <span key={t.at} className={`mttick ${t.reached ? 'on' : ''}`}
            style={{ gridColumn: `${t.at} / span ${seats - t.at + 1}` }}>
            {t.at} <span className="mttickword">{t.label}</span>
          </span>
        ))}
      </div>
      <p className="mtstanding">
        <span className="vh">
          {split.room + split.proxy} yes of {seats}
          {split.proxy > 0 ? `, ${split.proxy} by proxy` : ''}.{' '}
        </span>
        {track.state}
      </p>
    </div>
  );
}

interface ItemProps {
  n: number;
  p: RuleProposal;
  phase: Phase;
  docket: Docket;
  gm: string;
  actor: string;
  commish: boolean;
  proxies: readonly ProxyBallot[];
  onOpen: () => void;
  onReopen: () => void;
  /** Set the open item down without deciding it — see `tableItem`. */
  onTable: () => void;
  onVote: (voter: GmId, v: Vote | null) => void;
  onProvisional: (e: { vote: Vote; note?: string; shared?: boolean } | null) => void;
  onRecord: () => void;
}

function Item(props: ItemProps) {
  const { n, p, phase, docket, gm, commish, proxies } = props;
  /*
   * RECORDING IS THE IRREVERSIBLE-FEELING STEP, so it asks first. It writes the outcome onto the
   * proposal and is what `commitPlan` later folds into the rulebook; a mis-tap on a phone in a
   * room full of people talking is exactly how a rule gets recorded as failed when it passed.
   * The confirmation shows the count and every name behind it, because "are you sure" on its own
   * asks somebody to re-derive from memory what he is being asked to confirm.
   */
  const [confirming, setConfirming] = useState(false);
  /** Which GM's shared note is open on this item, if any. One at a time, per item. */
  const [openNote, setOpenNote] = useState<GmId | null>(null);
  const state = itemState(p.id, docket);
  const gate = canVote({ phase, item: state, recorded: Boolean(p.recordedAt) });
  const me = gm.toUpperCase();
  const votes = (p.votes ?? {}) as Record<string, Vote>;
  /*
   * REPRESENTED INCLUDES PROXIES (2015-I-1: "represented: either physically, virtually, or by
   * proxy"). The stored `represented` is the count a commissioner marked from the register, which
   * knows who is in the room and nothing about who lodged. Deriving it here is what makes two
   * proxies the difference between a meeting that cannot start and one that can.
   */
  const represented = representedSplit(GMS, docket.absent ?? [], proxies).total;
  const tally = tallyFrom(votes, represented);
  const result = tallyOutcome(tally, { isRevote: p.isRevote, proposerObjects: p.proposerObjects });
  const mine = p.provisional?.[me];
  const absent = new Set((docket.absent ?? []).map((a) => a.toUpperCase()));
  /*
   * ABSENT IS NOT THE SAME AS UNREPRESENTED — that distinction is the whole of 2015-I-1, and the
   * roll was flatly contradicting the quorum line six inches above it: two men marked "not
   * represented" while the header counted them toward nine.
   */
  const held = new Set(proxies.filter(isLodged).map((b) => b.gm.toUpperCase()));
  /*
   * IN THE ROOM, AN ITEM THAT IS NOT BEING VOTED IS A HEADING.
   *
   * The commissioner's board is a ten-name roll, a proxy panel and a tally — about 960px of it,
   * per item. Rendered for all six that put the live item 1,065px down a phone and made the stage
   * 6,011px tall, so the evening's actual question was a screen and a half below the fold and the
   * five answers nobody was being asked for were above it.
   *
   * What a decided item still owes the room is its outcome and a way back into it, and both
   * survive: the outcome is on the rail with its margin, and Reopen is still on the item. The
   * roll comes back the moment it is the item being voted on.
   */
  const quiet = phase === 'meeting' && state !== 'live';

  return (
    <li className={`mtitem ${state} ${p.recordedAt ? 'done' : ''}`} id={`item-${p.id}`}>
      <div className="mthead">
        <span className="mtno">{n}</span>
        <span className="mttitle">{p.title}</span>
        <span className={`mtstate ${state}`}>
          {state === 'live' ? 'Under discussion' : state === 'closed' ? 'Closed' : 'Locked'}
        </span>
        {p.recordedAt && (
          <span className={`mtout ${p.status}`}>{OUTCOME_LABEL[result.outcome]}</span>
        )}
      </div>

      {/* The live item expands to its full text — the thing being argued about, in the room. */}
      {state === 'live' && <RichView html={p.body} className="mtbody" />}
      {/*
        DRAWN ON THE ITEM BEING VOTED, prose everywhere else. The track needs a count to draw; on
        a locked item there is nothing to fill and ten empty cells state a threshold less clearly
        than the sentence does.
      */}
      {/*
        THE THRESHOLD IS A FACT ABOUT THE NIGHT, SAID ONCE (`design_handoff_docket`, defect 2).

        Every locked row used to carry `thresholdNote(p)` — the same twenty words, three times on
        one page, which is how a reader learns to skip the sentence that matters. The docket card
        states what tonight needs, once, at the top; the live item draws it as a track because it
        has a count to fill.

        THE ONE EXCEPTION IS A REAL ONE. On a night where somebody is unrepresented the relaxed
        six is available to ordinary items and never to a re-vote (2019-I-2 §2) — so on that
        night, and on that row alone, the item genuinely differs from what the header said.
      */}
      {phase === 'meeting' && state === 'live' ? (
        <ThresholdTrack p={p} votes={votes} represented={represented}
          absent={docket.absent ?? []} proxies={proxies} />
      ) : p.isRevote && represented < LEAGUE_SIZE ? (
        <p className="mtnote mut">
          A re-vote needs {SUPER_MAJORITY} either way — the relaxed {RELAXED_MAJORITY} does not
          reach it.
        </p>
      ) : null}

      {/* -- what a GM can do -------------------------------------------------------------- */}
      {!commish && (
        gate.allowed ? (
          <div className="mtvote">
            {mine && (
              <p className="mtrecall mut">
                You said <b>{mine.vote}</b> beforehand{mine.note ? ` — “${mine.note}”` : ''}.
              </p>
            )}
            <div className="mtbtns">
              {VOTES.map((v) => (
                <button key={v} type="button"
                  className={`mtbtn ${v} ${votes[me] === v ? 'on' : ''}`}
                  onClick={() => props.onVote(me, votes[me] === v ? null : v)}>
                  {v === 'abstain' ? 'Abstain' : v === 'yes' ? 'Yes' : 'No'}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <p className="mtclosed mut">{gate.why}</p>
            {/*
              WHEN A PRIVATE VOTE IS WORTH RECORDING — and it is a wider window than it was.
              It used to be phase 2 alone, on the reasoning that there is nothing to pre-vote on
              until the docket is set. That half is still right and is why nothing shows in the
              writing phase: a GM cannot see another GM's proposal before it is docketed.

              But it also vanished the instant the meeting opened, which is wrong in the other
              direction. The commissioner's board goes on READING these all through the meeting —
              that is what the note mark beside a name is — while the GM who had not got round to
              writing one lost the ability to. Somebody joining an hour in, on an item the room
              has not reached, is exactly the person with something to say and no way to say it.

              So: while the docket is out, and during the meeting on any item still locked. Never
              on the item under discussion, where the thing to do is vote.
            */}
            {(phase === 'docket' || (phase === 'meeting' && state === 'locked'))
              && <Provisional mine={mine} onSet={props.onProvisional} />}
          </>
        )
      )}

      {/* -- the commissioner's board ------------------------------------------------------ */}
      {commish && (
        <div className="mtboard">
          {!gate.allowed && <p className="mtclosed mut">{gate.why}</p>}
          {!quiet && (
          <ul className="mtgms">
            {GMS.map((g) => {
              const away = absent.has(g.toUpperCase());
              const prov = p.provisional?.[g.toUpperCase()];
              return (
                <li key={g}
                  className={`mtgm ${away ? 'away' : ''} `
                    + `${away && held.has(g.toUpperCase()) ? 'proxied' : ''}`}>
                  <span className="mtname">
                    <GmBadge gm={g} size="sm" />
                    <b>{managerShort(g)}</b>
                    {/*
                      A MARK, NOT A SENTENCE. This used to read "· said yes beforehand" appended to
                      the provenance line, which put a second GM's private reasoning in running
                      text on a card 112px wide and shifted every button under it. What a
                      commissioner needs from across the room is only whether there IS something to
                      read; the reading itself is a deliberate act.
                    */}
                    {prov?.shared && (
                      <button type="button" className={`mtnotebtn ${openNote === g ? 'on' : ''}`}
                        aria-expanded={openNote === g}
                        aria-label={`${managerShort(g)} left a note for the commissioners`}
                        title={`${managerShort(g)} left a note for the commissioners`}
                        onClick={() => setOpenNote(openNote === g ? null : g)}>
                        <NoteMark />
                      </button>
                    )}
                  </span>
                  {/*
                    THE BUTTONS COME BEFORE THE NOTE, which reads backwards and is deliberate.
                    Provenance is one line for most GMs, two for one of them and none at all for
                    the rest, so with the note above the buttons every card put its Y/N/– at a
                    different height and the row stopped being a row you can run a finger along.
                    Below them, the notes vary and the buttons still line up across the room.
                  */}
                  <span className="mtbtns">
                    {VOTES.map((v) => (
                      <button key={v} type="button" disabled={!gate.allowed}
                        className={`mtbtn ${v} ${votes[g.toUpperCase()] === v ? 'on' : ''}`}
                        onClick={() => props.onVote(g.toUpperCase(),
                          votes[g.toUpperCase()] === v ? null : v)}>
                        {v === 'abstain' ? '–' : v === 'yes' ? 'Y' : 'N'}
                      </button>
                    ))}
                  </span>
                  <span className="mtprov">
                    {provenance(p, g.toUpperCase(), managerShort)
                      || (away
                        ? (held.has(g.toUpperCase()) ? 'by proxy' : 'not represented')
                        : '')}
                  </span>
                </li>
              );
            })}
          </ul>
          )}

          {/*
            THE NOTE OPENS UNDER THE ROLL, NOT INSIDE THE CARD. In the card it was a column of
            three-word lines in 114px, and because grid rows stretch, opening one note grew all ten
            cards to match it. Down here it has the width of the board, one at a time, and the roll
            above it does not move.

            Commissioner-only by construction: this whole block is, and a provisional vote reaches
            it at all only because its author ticked "let the commissioners see this on the day".
          */}
          {!quiet && openNote && p.provisional?.[openNote.toUpperCase()]?.shared && (
            <div className="mtnoteopen">
              <b>
                {managerShort(openNote)} leans {p.provisional[openNote.toUpperCase()]!.vote}
              </b>
              {p.provisional[openNote.toUpperCase()]!.note && (
                <span className="mtnotewhy">
                  “{p.provisional[openNote.toUpperCase()]!.note}”
                </span>
              )}
              <span className="mut">Recorded before the meeting. Not a vote.</span>
              <button type="button" className="linkbtn tiny" onClick={() => setOpenNote(null)}>
                close
              </button>
            </div>
          )}

          {/*
            THE PROXIES, OPENED ONE ITEM AT A TIME.
            A commissioner may read the instruction for the item being called and nothing further
            down the docket. That is not a courtesy — `instructionFor` takes the OPEN item id
            rather than a boolean precisely so no caller can accidentally unseal a whole ballot,
            and `firestore.rules` refuses an unlodged one to anybody but its author.
          */}
          {!quiet && proxies.filter(isLodged).length > 0 && (
            <div className="mtproxies">
              <p className="mtproxyhd">
                <b>{proxies.filter(isLodged).length} held by proxy</b>
                <span className="mut">
                  {state === 'live' ? 'open for this item only' : 'sealed until an item is called'}
                </span>
              </p>
              <ul>
                {proxies.filter(isLodged).map((b) => {
                  const say = state === 'live' ? instructionFor(b, p.id) : null;
                  return (
                    <li key={b.gm}>
                      <GmBadge gm={b.gm} size="sm" />
                      <b>{managerShort(b.gm)}</b>
                      {state === 'live'
                        ? (
                          <span className={`mtproxysay ${say ?? 'blank'}`}>
                            {say ? say : 'no instruction — an abstention'}
                          </span>
                        )
                        : <span className="mut">Sealed</span>}
                      {say && commish && !p.recordedAt && gate.allowed && (
                        <button type="button" className="linkbtn tiny"
                          onClick={() => props.onVote(b.gm.toUpperCase(), say)}>
                          cast it
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!quiet && (
          <p className="mtcount">
            {tally.yes} yes · {tally.no} no · {tally.abstain} abstain · {represented} represented
            {!p.recordedAt && <span className="mut"> — {votesStillNeeded(tally, {
              isRevote: p.isRevote, proposerObjects: p.proposerObjects,
            })}</span>}
          </p>
          )}

          <div className="mtacts">
            {state !== 'live' && !docket.committedAt && (
              <button type="button" className="btn small"
                onClick={state === 'closed' ? props.onReopen : props.onOpen}>
                {state === 'closed' ? 'Reopen this vote' : 'Open this item'}
              </button>
            )}
            {/*
              THE WAY BACK OUT. Every other exit from `live` goes forwards — record an outcome, or
              close it as though the room had finished — so an item opened by accident could only
              be left parked or given a vote that never happened.
            */}
            {/*
              A RE-WORDED ITEM IS HELD OPEN (2019-I-2 §3). The absent GMs were sent the final text
              and have 24 hours on it; recording before that closes writes the rulebook over the
              top of people who have not answered yet.
            */}
            {state === 'live' && !canRecordAmended(p.amendedAt).allowed && (
              <p className="mtheld">{canRecordAmended(p.amendedAt).why}</p>
            )}
            {state === 'live' && commish && !p.recordedAt && !docket.committedAt
              && canRecordAmended(p.amendedAt).allowed && (
              <button type="button" className="btn small" onClick={props.onTable}>
                Table for now
              </button>
            )}
            {gate.allowed && !p.recordedAt && !confirming && (
              <button type="button" className="btn primary small" onClick={() => setConfirming(true)}>
                Record: {OUTCOME_LABEL[result.outcome]}
              </button>
            )}
          </div>

          {confirming && !p.recordedAt && (
            <RecordConfirm
              p={p} tally={tally} result={result} votes={votes} represented={represented}
              absent={absent}
              onCancel={() => setConfirming(false)}
              onConfirm={() => { setConfirming(false); props.onRecord(); }} />
          )}
        </div>
      )}
    </li>
  );
}


/**
 * The last look before an outcome is written down.
 *
 * WHAT IT SHOWS AND WHY. The count, because that is the thing being asserted. Every name under
 * the vote he cast, because a tally is a claim about ten people and the only way to check it is to
 * see them. The threshold sentence in the rulebook's own words, because "passed" and "carried for
 * one season" look identical on a button and are different rules. And, loudest, ANYONE WHO HAS NOT
 * VOTED — an abstention counts as a no under 2015-I-1, so a man the room simply has not got to yet
 * is arithmetically indistinguishable from one who declined, and recording early quietly converts
 * the first into the second.
 */
function RecordConfirm(
  { p, tally, result, votes, represented, absent, onCancel, onConfirm }: {
    p: RuleProposal;
    tally: ReturnType<typeof tallyFrom>;
    result: ReturnType<typeof tallyOutcome>;
    votes: Record<string, Vote>;
    represented: number;
    absent: Set<string>;
    onCancel: () => void;
    onConfirm: () => void;
  },
) {
  const cast = (v: Vote) => GMS.filter((g) => votes[g.toUpperCase()] === v);
  const silent = GMS.filter((g) => !votes[g.toUpperCase()] && !absent.has(g.toUpperCase()));

  return (
    <div className="mtconfirm" role="group" aria-label="Confirm the outcome">
      <p className="mtconfirmq">
        Record <b>{p.title}</b> as <b>{OUTCOME_LABEL[result.outcome]}</b>?
      </p>

      <p className="mtconfirmcount">
        {tally.yes} yes · {tally.no} no · {tally.abstain} abstain · {represented} of {LEAGUE_SIZE}
        {' '}represented
      </p>

      <ul className="mtconfirmwho">
        {([['yes', 'Yes'], ['no', 'No'], ['abstain', 'Abstained']] as const).map(([v, lab]) => {
          const names = cast(v);
          if (!names.length) return null;
          return (
            <li key={v}>
              <span className={`mtcvote ${v}`}>{lab}</span>
              <span className="mtcnames">{names.map((g) => managerShort(g)).join(', ')}</span>
            </li>
          );
        })}
      </ul>

      {silent.length > 0 && (
        <p className="note bad mtconfirmwarn">
          {silent.length === 1
            ? `${managerShort(silent[0]!)} has not voted`
            : `${silent.length} have not voted — ${silent.map((g) => managerShort(g)).join(', ')}`}
          . Recording now counts them against it (<Citations ids={['2015-I-1']} />).
        </p>
      )}

      <p className="note mtconfirmwhy">{result.summary}</p>

      <div className="mtacts">
        <button type="button" className="btn small" onClick={onCancel}>Not yet</button>
        <button type="button" className="btn primary small" onClick={onConfirm}>
          Record: {OUTCOME_LABEL[result.outcome]}
        </button>
      </div>
    </div>
  );
}

/**
 * A private provisional vote, before the meeting.
 *
 * It is not a vote and the copy says so twice — on the control and on the confirmation — because
 * the one misunderstanding that matters here is somebody recording this and believing he has
 * voted.
 */
/**
 * The mark that says a GM left something for the commissioners to read.
 *
 * Drawn to `NavIcons`' rules — 24-unit grid, 1.5 stroke, `currentColor`, no fill — so it sits
 * beside a name at 14px without becoming a second badge competing with the face.
 */
function NoteMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6.5 3H14l4 4v13.5a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-17a.5.5 0 0 1 .5-.5z" />
      <path d="M14 3v4h4" />
      <path d="M9 12.5h6M9 16h4" />
    </svg>
  );
}

function Provisional(
  { mine, onSet }:
  {
    mine?: { vote: Vote; note?: string; shared?: boolean };
    onSet: (e: { vote: Vote; note?: string; shared?: boolean } | null) => void;
  },
) {
  const [open, setOpen] = useState(false);
  const [vote, setVote] = useState<Vote>(mine?.vote ?? 'yes');
  const [note, setNote] = useState(mine?.note ?? '');
  const [shared, setShared] = useState(Boolean(mine?.shared));

  if (mine && !open) {
    return (
      <p className="mtprovnote">
        <NoteMark />
        {' '}You lean <b>{mine.vote}</b>{mine.note ? ` — “${mine.note}”` : ''}.
        {mine.shared ? ' Shared with the commissioners.' : ' Private.'}
        {' '}
        <button type="button" className="linkbtn tiny" onClick={() => setOpen(true)}>change</button>
        {' '}
        <button type="button" className="linkbtn tiny" onClick={() => onSet(null)}>withdraw</button>
      </p>
    );
  }
  if (!open) {
    /*
     * A BUTTON, NOT A FOOTNOTE (Tim, 26 August: "I'm not seeing the place we designed for GMs to
     * privately record their votes"). This was six words of `linkbtn tiny` under a grey line
     * explaining why you cannot vote yet, on a card already full of small grey text — which is
     * indistinguishable from a caption. It is the only way a GM says anything at all between the
     * docket going out and the meeting, so it gets to look like a control.
     */
    return (
      <button type="button" className="btn small mtprovopen" onClick={() => setOpen(true)}>
        <NoteMark />
        Record where you stand — privately
      </button>
    );
  }
  return (
    <div className="mtprovform">
      <div className="mtbtns">
        {VOTES.map((v) => (
          <button key={v} type="button" className={`mtbtn ${v} ${vote === v ? 'on' : ''}`}
            onClick={() => setVote(v)}>
            {v === 'abstain' ? 'Abstain' : v === 'yes' ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
      <input className="search" value={note} placeholder="Why — optional"
        onChange={(e) => setNote(e.target.value)} />
      <label className="mtshare">
        <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
        <span>Let the commissioners see this on the day</span>
      </label>
      <div className="mtacts">
        <button type="button" className="btn small"
          onClick={() => { onSet({ vote, note: note.trim() || undefined, shared }); setOpen(false); }}>
          Save
        </button>
        <button type="button" className="linkbtn" onClick={() => setOpen(false)}>cancel</button>
      </div>
    </div>
  );
}
